import React, { useEffect, useState, useRef } from 'react';
import {
    Box, Stack, Grid, FormControl,
    CircularProgress, Button, Alert, Snackbar, TextField,
    Paper, Typography, IconButton, Divider, Autocomplete,
    Dialog, DialogActions, DialogContent, Checkbox, FormControlLabel
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../contexts/auth.context';
import { useEvent } from '../../contexts/event.context';
import api from '../../services/api';
import * as qs from 'qs';
import { maskCPF2, maskCNPJ } from '../../utils/mask';

export default function Crachas() {
    const { basePath } = useAuth();
    const { events, getEvents, pagination } = useEvent();
    const [loading, setLoading] = useState(false);
    const [subscribers, setSubscribers] = useState([]);
    const [searchCpf, setSearchCpf] = useState('');
    const [searchName, setSearchName] = useState('');
    const [searchCnpj, setSearchCnpj] = useState('');
    const [searchCompanyName, setSearchCompanyName] = useState('');
    const [stateAlert, setStateAlert] = useState({ status: false, type: 'success', msg: '' });
    const [printingId, setPrintingId] = useState(null);
    const [eventSelected, setEventSelected] = useState(null);
    const [eventOptions, setEventOptions] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [hasMoreEvents, setHasMoreEvents] = useState(true);
    const pageEvents = useRef(1);
    const [openPrintModal, setOpenPrintModal] = useState(false);
    const [pdfData, setPdfData] = useState(null);
    const printIframeRef = useRef(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [printingBatch, setPrintingBatch] = useState(false);

    async function handleSearch() {
        if (!eventSelected) {
            setStateAlert({ status: true, type: 'warning', msg: 'Por favor, selecione um evento primeiro.' });
            return;
        }

        setLoading(true);
        
        try {
            // Construir filtros dinâmicos
            let filters = {
                deleted: { $eq: false },
                event: { name: eventSelected }
            };

            // Filtros de subscribers
            let subscriberFilters = {};
            
            if (searchCpf) {
                subscriberFilters.document = { $contains: searchCpf };
            }
            
            if (searchName) {
                subscriberFilters.name = { $containsi: searchName };
            }

            // Filtros de empresa
            let companyFilters = {};
            
            if (searchCnpj) {
                companyFilters.cnpj = { $contains: searchCnpj };
            }
            
            if (searchCompanyName) {
                companyFilters.$or = [
                    { social_reason: { $containsi: searchCompanyName } },
                    { trade: { $containsi: searchCompanyName } }
                ];
            }

            // Adicionar filtros se existirem
            if (Object.keys(subscriberFilters).length > 0) {
                filters.subscribers = subscriberFilters;
            }

            if (Object.keys(companyFilters).length > 0) {
                filters.company = companyFilters;
            }

            const params = {
                sort: ['createdAt:desc'],
                pagination: { page: 1, pageSize: 50 },
                filters: filters,
                populate: {
                    event: { fields: ['name'] },
                    plan: { fields: ['type'] },
                    company: { fields: ['city', 'state', 'social_reason', 'trade', 'document'] },
                    subscribers: { fields: ['id', 'name', 'badge', 'document', 'whatsapp', 'email'] }
                },
                encodeValuesOnly: true
            };

            const query = qs.stringify(params, {
                skipNulls: false,
                skipUndefined: false,
                skipEmptyStrings: true
            });

            const response = await api.get(`/orders/?${query}`);
            const ordersData = response.data.data;

            if (!ordersData || ordersData.length === 0) {
                setSubscribers([]);
                setStateAlert({ status: true, type: 'info', msg: 'Nenhum inscrito encontrado com os critérios informados.' });
                setLoading(false);
                return;
            }

            // Transformar dados para exibição
            const subscribersData = ordersData.flatMap(order => {
                const company = order.attributes.company?.data?.attributes || {};
                const event = order.attributes.event?.data?.attributes?.name || 'N/A';
                const plan = order.attributes.plan?.data?.attributes?.type || 'N/A';
                
                return order.attributes.subscribers.data.map(subscriber => ({
                    id: subscriber.id,
                    name: subscriber.attributes.name,
                    badge: subscriber.attributes.badge || 'N/A',
                    document: maskCPF2(subscriber.attributes.document) || 'N/A',
                    email: subscriber.attributes.email || 'N/A',
                    whatsapp: subscriber.attributes.whatsapp || 'N/A',
                    company: company.social_reason || company.trade || 'N/A',
                    cnpj: maskCNPJ(company.document) || 'N/A',
                    city: company.city || 'N/A',
                    state: company.state || 'N/A',
                    event: event,
                    category: plan
                }));
            });

            setSubscribers(subscribersData);
            setStateAlert({ status: true, type: 'success', msg: `${subscribersData.length} inscrito(s) encontrado(s).` });

        } catch (error) {
            console.error('Erro ao buscar inscritos:', error);
            setStateAlert({ status: true, type: 'error', msg: `Erro ao buscar inscritos: ${error.message}` });
            setSubscribers([]);
        } finally {
            setLoading(false);
        }
    }

    async function handlePrintLabel(subscriberId) {
        setPrintingId(subscriberId);
        
        try {
            const response = await api.post(`order/label`, { subscriber: subscriberId, duplicate: true });

            if (response.status === 200) {
                const base64Pdf = response.data.pdf;
                
                // Converter base64 para Blob para evitar problemas de CORS
                const byteCharacters = atob(base64Pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                
                setPdfData(url);
                setOpenPrintModal(true);
                setStateAlert({ status: true, type: 'success', msg: 'Enviando para impressão...' });
            }
        } catch (error) {
            console.error('Erro ao gerar crachá:', error);
            setStateAlert({ status: true, type: 'error', msg: `Erro ao gerar crachá: ${error.message}` });
        } finally {
            setPrintingId(null);
        }
    }

    function handlePrint() {
        if (printIframeRef.current) {
            try {
                printIframeRef.current.contentWindow.print();
            } catch (error) {   
                console.error('Erro ao imprimir:', error);
                setStateAlert({ status: true, type: 'error', msg: 'Erro ao enviar para impressão' });
            }
        }
    }

    function handleCloseModal() {
        setOpenPrintModal(false);
        if (pdfData) {
            URL.revokeObjectURL(pdfData);
        }
        setPdfData(null);
    }

    // Efeito para imprimir automaticamente quando o PDF carregar
    useEffect(() => {
        if (openPrintModal && pdfData && printIframeRef.current) {
            const iframe = printIframeRef.current;
            
            const handleLoad = () => {
                setTimeout(() => {
                    try {
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();
                    } catch (error) {
                        console.error('Erro ao imprimir automaticamente:', error);
                    }
                }, 500);
            };

            iframe.addEventListener('load', handleLoad);
            
            return () => {
                iframe.removeEventListener('load', handleLoad);
            };
        }
    }, [openPrintModal, pdfData]);

    function handleClearFilters() {
        setSearchCpf('');
        setSearchName('');
        setSearchCnpj('');
        setSearchCompanyName('');
        setSubscribers([]);
        setSelectedIds([]);
    }

    function handleToggleSelect(id) {
        setSelectedIds(prev => 
            prev.includes(id) 
                ? prev.filter(selectedId => selectedId !== id)
                : [...prev, id]
        );
    }

    function handleSelectAll() {
        setSelectedIds(subscribers.map(sub => sub.id));
    }

    function handleDeselectAll() {
        setSelectedIds([]);
    }

    async function handlePrintBatch() {
        if (selectedIds.length === 0) {
            setStateAlert({ status: true, type: 'warning', msg: 'Selecione ao menos um inscrito para imprimir.' });
            return;
        }

        setPrintingBatch(true);
        
        try {
            // Enviar todos os IDs de uma vez para gerar um único PDF
            const response = await api.post(`order/selected-labels`, { subscribers: selectedIds, duplicate: true });

            if (response.status === 200) {
                const base64Pdf = response.data.pdf;
                const byteCharacters = atob(base64Pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                
                setPdfData(url);
                setOpenPrintModal(true);
                setStateAlert({ status: true, type: 'success', msg: `PDF gerado com ${selectedIds.length} crachá(s)!` });
            }
        } catch (error) {
            console.error('Erro ao gerar PDF em lote:', error);
            setStateAlert({ status: true, type: 'error', msg: `Erro ao gerar PDF: ${error.message}` });
        } finally {
            setPrintingBatch(false);
        }
    }

    async function loadMoreEvents() {
        if (!hasMoreEvents || loadingEvents) return;

        setLoadingEvents(true);
        try {
            await getEvents(pageEvents.current + 1, pagination.pageSize, true);
            setEventOptions(prev => [...prev, ...events]);
        } catch (error) {
            console.error('Erro ao carregar eventos:', error);
        } finally {
            setLoadingEvents(false);
        }
    }

    useEffect(() => {
        async function loadInitialEvents() {
            setLoadingEvents(true);
            try {
                await getEvents(1, 20);
                setEventOptions(events);
            } catch (error) {
                console.error('Erro ao carregar eventos:', error);
            } finally {
                setLoadingEvents(false);
            }
        }
        loadInitialEvents();
    }, []);

    useEffect(() => {
        if (events && events.length > 0) {
            setEventOptions(events);
        }
    }, [events]);

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#f5f5f5' }} p={3}>
            <Snackbar
                open={stateAlert.status}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => setStateAlert({ status: false, type: 'success', msg: '' })}
            >
                <Alert sx={{ width: '100%' }} severity={stateAlert.type}>
                    {stateAlert.msg}
                </Alert>
            </Snackbar>

            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                    Emissão de Crachás
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={12}>
                        <FormControl fullWidth>
                            <Autocomplete
                                id="select-event-infinite-scroll"
                                options={eventOptions}
                                loading={loadingEvents}
                                getOptionLabel={(option) => option.attributes?.name || ''}
                                value={eventOptions.find(e => e.attributes?.name === eventSelected) || null}
                                onChange={(_, newValue) => {
                                    if (newValue) {
                                        setEventSelected(newValue.attributes.name);
                                        setSubscribers([]);
                                        handleClearFilters();
                                    } else {
                                        setEventSelected(null);
                                        setSubscribers([]);
                                        handleClearFilters();
                                    }
                                }}
                                onOpen={() => {
                                    if (eventOptions.length === 0) {
                                        loadMoreEvents();
                                    }
                                }}
                                ListboxProps={{
                                    onScroll: (event) => {
                                        const listboxNode = event.currentTarget;
                                        if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
                                            pageEvents.current += 1;
                                            loadMoreEvents();
                                        }
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Selecionar Evento *"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loadingEvents ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="CPF do Inscrito"
                            variant="outlined"
                            value={searchCpf}
                            onChange={(e) => {
                                const value = e.target.value;
                                const masked = maskCPF2(value);
                                if (masked.length <= 14) {
                                    setSearchCpf(masked);
                                }
                            }}
                            placeholder="000.000.000-00"
                            disabled={!eventSelected}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                            inputProps={{ maxLength: 14 }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Nome do Inscrito"
                            variant="outlined"
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            placeholder="Digite o nome"
                            disabled={!eventSelected}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="CNPJ da Empresa"
                            variant="outlined"
                            value={searchCnpj}
                            onChange={(e) => {
                                const value = e.target.value;
                                const masked = maskCNPJ(value);
                                if (masked.length <= 18) {
                                    setSearchCnpj(masked);
                                }
                            }}
                            placeholder="00.000.000/0000-00"
                            disabled={!eventSelected}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                            inputProps={{ maxLength: 18 }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Nome da Empresa"
                            variant="outlined"
                            value={searchCompanyName}
                            onChange={(e) => setSearchCompanyName(e.target.value)}
                            placeholder="Razão social ou fantasia"
                            disabled={!eventSelected}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleClearFilters}
                                disabled={loading || !eventSelected}
                            >
                                Limpar Filtros
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SearchIcon />}
                                onClick={handleSearch}
                                disabled={loading || !eventSelected}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Buscar'}
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '30vh' }}>
                    <CircularProgress size={60} />
                </Box>
            ) : subscribers.length > 0 ? (
                <Paper elevation={3} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Inscritos Encontrados ({subscribers.length})
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleSelectAll}
                                disabled={selectedIds.length === subscribers.length}
                            >
                                Selecionar Todos
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleDeselectAll}
                                disabled={selectedIds.length === 0}
                            >
                                Desmarcar Todos
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={printingBatch ? null : <PrintIcon />}
                                onClick={handlePrintBatch}
                                disabled={selectedIds.length === 0 || printingBatch}
                            >
                                {printingBatch ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    `Imprimir Selecionados (${selectedIds.length})`
                                )}
                            </Button>
                        </Stack>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2}>
                        {subscribers.map((subscriber) => (
                            <Grid item xs={12} key={subscriber.id}>
                                <Paper
                                    elevation={1}
                                    sx={{
                                        p: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        '&:hover': {
                                            backgroundColor: '#f9f9f9',
                                            boxShadow: 3
                                        }
                                    }}
                                >
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={6} md="auto">
                                            <Checkbox
                                                checked={selectedIds.includes(subscriber.id)}
                                                onChange={() => handleToggleSelect(subscriber.id)}
                                                color="primary"
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6} md={2.5}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Nome
                                            </Typography>
                                            <Typography variant="body1" fontWeight={500}>
                                                {subscriber.name}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={2}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                CPF
                                            </Typography>
                                            <Typography variant="body2">
                                                {subscriber.document}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={1.5}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Crachá
                                            </Typography>
                                            <Typography variant="body2">
                                                {subscriber.badge}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={2.5}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Empresa
                                            </Typography>
                                            <Typography variant="body2" noWrap>
                                                {subscriber.company}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={1}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Cidade/UF
                                            </Typography>
                                            <Typography variant="body2">
                                                {subscriber.city}/{subscriber.state}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={1}>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                startIcon={printingId === subscriber.id ? null : <PrintIcon />}
                                                onClick={() => handlePrintLabel(subscriber.id)}
                                                disabled={printingId === subscriber.id}
                                                fullWidth
                                            >
                                                {printingId === subscriber.id ? (
                                                    <CircularProgress size={20} color="inherit" />
                                                ) : (
                                                    'Imprimir'
                                                )}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            ) : (
                <Paper elevation={3} sx={{ p: 5, textAlign: 'center' }}>
                    <Typography variant="h6" color="textSecondary">
                        {!eventSelected 
                            ? 'Selecione um evento para começar' 
                            : 'Utilize os filtros acima para buscar inscritos e emitir crachás'
                        }
                    </Typography>
                </Paper>
            )}

            {/* Modal de Impressão */}
            <Dialog
                open={openPrintModal}
                onClose={handleCloseModal}
                maxWidth="md"
                fullWidth
            >
                <DialogContent sx={{ p: 0, height: '80vh' }}>
                    {pdfData && (
                        <iframe
                            ref={printIframeRef}
                            src={pdfData}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none'
                            }}
                            title="Visualização do Crachá"
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="secondary">
                        Fechar
                    </Button>
                    <Button
                        onClick={handlePrint}
                        variant="contained"
                        color="primary"
                        startIcon={<PrintIcon />}
                    >
                        Imprimir
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
